import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import supabase from "../../../../../utils/supabase";
import { fetchScientificNamesByParent, fetchScientificNames } from "./scientificNameService";
import debounce from "lodash/debounce";

export default function DialogScientificNameBulkMoveChildren({ open, onClose, sourceParent }) {
  const [childrenRows, setChildrenRows] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  
  const [newParent, setNewParent] = useState(null);
  const [parentOptions, setParentOptions] = useState([]);
  const [loadingParentSearch, setLoadingParentSearch] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch children on open
  useEffect(() => {
    if (open && sourceParent?.id) {
      setLoadingChildren(true);
      fetchScientificNamesByParent({ parentId: sourceParent.id })
        .then(({ rows }) => {
          const safeRows = rows || [];
          setChildrenRows(safeRows);
          // Default select all
          setSelectionModel(safeRows.map((r) => r.id));
        })
        .catch((err) => {
          console.error("Failed to fetch children", err);
          setError("Failed to load children.");
        })
        .finally(() => setLoadingChildren(false));
    }
  }, [open, sourceParent]);

  // Parent search
  const fetchParents = useMemo(
    () =>
      debounce((request, callback) => {
        fetchScientificNames({
          page: 0,
          pageSize: 20,
          search: request.input,
          orderBy: "name_spell_valid",
          ascending: true,
        })
          .then(({ rows }) => callback(rows))
          .catch(() => callback([]));
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setParentOptions([]);
      return undefined;
    }

    setLoadingParentSearch(true);
    fetchParents({ input: inputValue }, (results) => {
      if (active) {
        setParentOptions(results || []);
        setLoadingParentSearch(false);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetchParents]);

  const handleMove = async () => {
    if (!newParent?.id) return;
    if (selectionModel.length === 0) return;
    if (newParent.id === sourceParent.id) {
      setError("New parent cannot be the same as current parent.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from("scientific_names")
        .update({ current_parent: newParent.id })
        .in("id", selectionModel);

      if (updateError) throw updateError;

      onClose(true); // true indicates success/refresh needed
    } catch (err) {
      console.error("Move failed", err);
      setError(err.message || "Failed to move children.");
    } finally {
      setSubmitting(false);
    }
  };

  const sourceName = sourceParent?._nameValidDisp || sourceParent?.name_spell_valid || sourceParent?.id;

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>Move Children of: {sourceName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}
          
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              1. Select children to move ({selectionModel.length} selected)
            </Typography>
            <Box sx={{ height: 300, width: "100%" }}>
              <DataGrid
                rows={childrenRows}
                columns={[
                  { field: "name_spell_valid", headerName: "Name", flex: 1 },
                  { field: "current_rank", headerName: "Rank", width: 120 },
                  { field: "authority_year", headerName: "Year", width: 100 },
                ]}
                getRowId={(r) => r.id}
                checkboxSelection
                // Conditionally pass the props matching the shape we hold in state.
                // If `selectionModel` is an Array (legacy), pass `selectionModel` +
                // `onSelectionModelChange`. If it's an object (new API), pass
                // `rowSelectionModel` + `onRowSelectionModelChange`.
                {...(Array.isArray(selectionModel)
                  ? {
                      selectionModel: selectionModel,
                      onSelectionModelChange: (m) => {
                        let next = [];
                        try {
                          if (Array.isArray(m)) next = m;
                          else if (m && typeof m === "object") {
                            if (Array.isArray(m.selectionModel)) next = m.selectionModel;
                            else if (m?.selectionModel?.ids) next = Array.from(m.selectionModel.ids);
                            else if (Array.isArray(m.ids)) next = m.ids;
                            else if (m.ids) next = Array.from(m.ids);
                          }
                        } catch (e) {
                          console.error("selection normalization failed", e, m);
                        }
                        setSelectionModel(next);
                      },
                    }
                  : {
                      rowSelectionModel: selectionModel,
                      onRowSelectionModelChange: (m) => {
                        let next = [];
                        try {
                          if (Array.isArray(m)) next = m;
                          else if (m && typeof m === "object") {
                            if (Array.isArray(m.selectionModel)) next = m.selectionModel;
                            else if (m?.selectionModel?.ids) next = Array.from(m.selectionModel.ids);
                            else if (Array.isArray(m.ids)) next = m.ids;
                            else if (m.ids) next = Array.from(m.ids);
                          }
                        } catch (e) {
                          console.error("rowSelection normalization failed", e, m);
                        }
                        setSelectionModel(next);
                      },
                    })}
                loading={loadingChildren}
                keepNonExistentRowsSelected={false}
                disableRowSelectionOnClick
                initialState={{
                  pagination: { paginationModel: { pageSize: 100 } },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              2. Select new parent
            </Typography>
            <Autocomplete
              getOptionLabel={(option) =>
                `${option.name_spell_valid || option.id} [${option.current_rank || "?"}]`
              }
              filterOptions={(x) => x}
              options={parentOptions}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={newParent}
              onChange={(event, newValue) => {
                setParentOptions(newValue ? [newValue, ...parentOptions] : parentOptions);
                setNewParent(newValue);
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search new parent (name, author...)"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingParentSearch ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Box>
                      <Typography variant="body1">
                        {option.name_spell_valid}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.current_rank} | {option.authority_year} | {option.author}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleMove}
          disabled={submitting || !newParent || selectionModel.length === 0}
        >
          {submitting ? "Moving..." : "Move Selected Children"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}